// Listing form limits. The numbers are NOT defined here: they come from the backend
// (GET /api/listing-rules, editable by admins on the "Listing Rules" page) via useListingLimits().
// This file only holds the pure helpers that apply those limits.

// API rule key -> property name used by the forms (L.TITLE_MIN, ...).
const KEY_MAP = {
  title_min: 'TITLE_MIN',
  title_max: 'TITLE_MAX',
  description_max: 'DESCRIPTION_MAX',
  bathrooms_max: 'BATHROOMS_MAX',
  size_max: 'SIZE_MAX',
  bedrooms_max: 'BEDROOMS_MAX',
  guests_per_room_max: 'GUESTS_PER_ROOM_MAX',
  beds_per_room_max: 'BEDS_PER_ROOM_MAX',
  total_guests_max: 'TOTAL_GUESTS_MAX',
  price_min: 'PRICE_MIN',
  price_max: 'PRICE_MAX',
  tax_percent_max: 'PERCENT_MAX',
  extra_fees_max: 'EXTRA_FEES_MAX',
  images_max: 'IMAGES_MAX',
  image_max_mb: 'IMAGE_MAX_MB',
  booking_days_max: 'BOOKING_DAYS_MAX',
};

export const buildLimits = (values) => {
  const limits = {};
  Object.entries(KEY_MAP).forEach(([apiKey, prop]) => {
    limits[prop] = Number(values?.[apiKey]);
  });
  return limits;
};

// Top-level numeric form fields -> { max, integer }.
export const numericFieldRules = (L) => ({
  bathrooms: { max: L.BATHROOMS_MAX, integer: true },
  size: { max: L.SIZE_MAX, integer: true },
  nightly: { max: L.PRICE_MAX },
  weekends: { max: L.PRICE_MAX },
  weekly_per_night: { max: L.PRICE_MAX },
  monthly_per_night: { max: L.PRICE_MAX },
  cleaning_fee_amount: { max: L.PRICE_MAX },
  security_deposit: { max: L.PRICE_MAX },
  local_tax_percent: { max: L.PERCENT_MAX },
  other_regional_taxes: { max: L.PERCENT_MAX },
  value_added_tax: { max: L.PERCENT_MAX },
});

const isEmpty = (v) => v === '' || v === null || v === undefined;

// Digits only, clamped to max (used while typing).
export const sanitizeInteger = (value, max) => {
  if (isEmpty(value)) return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  return String(Math.min(parseInt(digits, 10), max));
};

// Non-negative decimal with up to `decimals` places, clamped to max (used while typing).
export const sanitizeDecimal = (value, max, decimals = 2) => {
  if (isEmpty(value)) return '';
  let str = String(value).replace(/[^\d.]/g, '');
  const [whole, ...rest] = str.split('.');
  str = rest.length ? `${whole}.${rest.join('').slice(0, decimals)}` : whole;
  if (str === '' || str === '.') return str === '.' ? '0.' : '';
  const num = parseFloat(str);
  return !isNaN(num) && num > max ? String(max) : str;
};

// Sanitizes a top-level numeric field while typing; returns undefined for non-numeric fields.
export const sanitizeField = (name, value, L) => {
  const rule = numericFieldRules(L)[name];
  if (!rule) return undefined;
  return rule.integer ? sanitizeInteger(value, rule.max) : sanitizeDecimal(value, rule.max);
};

// Returns an error message, or '' when valid.
export const checkNumber = (value, { label, min = 0, max, integer = false, decimals = 2 }) => {
  if (isEmpty(value)) return `${label} is required`;
  const str = String(value).trim();
  const pattern = integer ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{1,${decimals}}0*)?$`); // trailing zeros OK: DB returns "100.0000"
  if (!pattern.test(str)) {
    return integer
      ? `${label} must be a whole number`
      : `${label} must be a valid number (max ${decimals} decimals)`;
  }
  const num = Number(str);
  if (num < min) return `${label} must be at least ${min}`;
  if (max !== undefined && num > max) return `${label} must not exceed ${max}`;
  return '';
};

// Same as checkNumber but an empty value is allowed.
export const checkOptionalNumber = (value, opts) => (isEmpty(value) ? '' : checkNumber(value, opts));
